import type { UsBrowserLineQuote, UsLineIdentity } from './usBoardQuotes';
import type { GameEventRow, GameRow, SpreadLine, TotalLine } from '../views/ValueDashboard/types';
import { normalizeUsGameKey } from '../lib/polymarketUs/sports';
import { usLinePillCents, usSpreadCell, usTotalCell } from '../views/SpreadsTotals/usLineCell';

/** #1262: append independently listed native US lines to the common rows.
 * A line has its own US identity/quote, never a fabricated K/P venue book.
 * `games` already applied the board's date/status and identity rules. */
export function mergeUsStandaloneLines(rows: GameEventRow[], inventory: readonly UsLineIdentity[], games: readonly GameRow[],
  quotes: (key: string) => { spreads: UsBrowserLineQuote[]; totals: UsBrowserLineQuote[] }): void {
  const grouped = new Map<string, UsLineIdentity[]>();
  for (const line of inventory) {
    const list = grouped.get(line.game.key) ?? []; list.push(line); grouped.set(line.game.key, list);
  }
  const eventByKey = new Map(rows.map(row => [normalizeUsGameKey(row.key, row.sport.toUpperCase() as UsLineIdentity['game']['sport']), row]));
  const gameByKey = new Map(games.map(row => [normalizeUsGameKey(row.key, row.sport.toUpperCase() as UsLineIdentity['game']['sport']), row]));
  for (const [key, identities] of grouped) {
    let event = eventByKey.get(key);
    const game = gameByKey.get(key);
    if (!event && !game) continue;
    if (!event && game) {
      event = { key: game.key, sport: game.sport, date: game.date, admissionDate: game.admissionDate,
        timePt: game.timePt, startTimeMs: game.startTimeMs, gameState: game.gameState, gameStateAuthority: game.gameStateAuthority,
        awayCode: game.awayCode, homeCode: game.homeCode, awayName: game.awayName, homeName: game.homeName,
        spreads: [], totals: [], period: 'full' };
      rows.push(event);
    }
    if (!event) continue;
    const candidates = quotes(key);
    for (const identity of identities) {
      if (identity.marketType === 'spreads') {
        const code = identity.favored === 'away' ? identity.game.awayCode : identity.game.homeCode;
        if (code !== event.awayCode && code !== event.homeCode) continue;
        const favored = code === event.awayCode ? 'away' : 'home';
        const line = (favored === 'away' ? -1 : 1) * Math.abs(identity.line);
        let row = event.spreads.find(row => row.favored === favored && row.line === line);
        if (!row) { row = { line, favored }; event.spreads.push(row); }
        row.polymarketUs = display(row.polymarketUs?.marketSlugs ?? [], identity.marketSlug,
          usSpreadCell(event, row, candidates.spreads));
      } else {
        let row = event.totals.find(row => row.line === identity.line);
        if (!row) { row = { line: identity.line }; event.totals.push(row); }
        row.polymarketUs = display(row.polymarketUs?.marketSlugs ?? [], identity.marketSlug,
          usTotalCell(event, row, candidates.totals));
      }
    }
    event.spreads.sort((a, b) => a.line - b.line);
    event.totals.sort((a, b) => a.line - b.line);
  }
}
function display(slugs: readonly string[], slug: string, cell: Parameters<typeof usLinePillCents>[0]): NonNullable<SpreadLine['polymarketUs'] | TotalLine['polymarketUs']> {
  const bid = usLinePillCents(cell, 'yes', 'maker'), ask = usLinePillCents(cell, 'yes', 'taker');
  return { marketSlugs: slugs.includes(slug) ? slugs : [...slugs, slug], priceCents: bid !== null && ask !== null ? (bid + ask) / 2 : null };
}
